import React from 'react';
import { Form, FormikHelpers, FormikValues, useFormikContext } from 'formik';
import { AnyObjectSchema } from 'yup';
import { Button, IconButton, Stack } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { MuiForm } from 'lib/Form';
import { ReadOnlyStatus, SetupSectionCard, StatusChip } from './SetupSectionPrimitives';

interface HeaderEndProps {
    isConfigured: boolean;
    isReadOnly: boolean;
    isEditing: boolean;
    onEdit: () => void;
}

const HeaderEnd: React.FC<HeaderEndProps> = ({ isConfigured, isReadOnly, isEditing, onEdit }) => (
    <Stack direction="row" spacing={1} alignItems="center">
        <StatusChip configured={isConfigured} />
        {!isReadOnly && !isEditing && (
            <IconButton size="small" onClick={onEdit} aria-label="Edit section">
                <EditOutlinedIcon fontSize="small" />
            </IconButton>
        )}
    </Stack>
);

interface FormActionsProps {
    onCancel: () => void;
}

const FormActions: React.FC<FormActionsProps> = ({ onCancel }) => {
    const { submitForm, isSubmitting, isValid, resetForm } = useFormikContext();
    const handleCancel = React.useCallback(() => {
        resetForm();
        onCancel();
    }, [resetForm, onCancel]);

    return (
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="outlined" size="small" onClick={handleCancel} disabled={isSubmitting}>
                Cancel
            </Button>
            <Button variant="contained" size="small" onClick={submitForm} disabled={isSubmitting || !isValid}>
                Save
            </Button>
        </Stack>
    );
};

interface EditableSettingsSectionProps<V extends FormikValues> {
    title: string;
    subtitle: React.ReactNode;
    readOnlySubtitle?: React.ReactNode;
    description?: React.ReactNode;
    isConfigured: boolean;
    isReadOnly: boolean;
    initialValues: V;
    validationSchema?: AnyObjectSchema;
    onSave: (values: V) => Promise<boolean>;
    renderFields: (isEditing: boolean) => React.ReactNode;
    renderReadOnlyRows?: () => React.ReactNode;
}

/**
 * Shared scaffold for a setup section. Handles the view ⇄ edit toggle, the Save/Cancel actions and
 * the read-only presentation, so each section component only supplies its fields and persistence.
 */
export function EditableSettingsSection<V extends FormikValues>({
    title,
    subtitle,
    readOnlySubtitle,
    description,
    isConfigured,
    isReadOnly,
    initialValues,
    validationSchema,
    onSave,
    renderFields,
    renderReadOnlyRows
}: EditableSettingsSectionProps<V>) {
    const [isEditing, setIsEditing] = React.useState(false);

    const handleEdit = React.useCallback(() => setIsEditing(true), []);
    const handleExitEdit = React.useCallback(() => setIsEditing(false), []);

    const handleSubmit = React.useCallback(
        async (values: V, helpers: FormikHelpers<V>) => {
            try {
                const saved = await onSave(values);
                if (saved) {
                    setIsEditing(false);
                }
            } finally {
                helpers.setSubmitting(false);
            }
        },
        [onSave]
    );

    if (isReadOnly && renderReadOnlyRows) {
        return (
            <SetupSectionCard
                title={title}
                subtitle={readOnlySubtitle ?? subtitle}
                headerEnd={<ReadOnlyStatus configured={isConfigured} />}
            >
                {renderReadOnlyRows()}
            </SetupSectionCard>
        );
    }

    return (
        <MuiForm<V>
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnMount
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {() => (
                <Form>
                    <SetupSectionCard
                        title={title}
                        subtitle={subtitle}
                        headerEnd={
                            <HeaderEnd
                                isConfigured={isConfigured}
                                isReadOnly={isReadOnly}
                                isEditing={isEditing}
                                onEdit={handleEdit}
                            />
                        }
                    >
                        {description}
                        {renderFields(isEditing && !isReadOnly)}
                        {isEditing && !isReadOnly && <FormActions onCancel={handleExitEdit} />}
                    </SetupSectionCard>
                </Form>
            )}
        </MuiForm>
    );
}
